import moment from 'moment';
import addStartupScript from '../events/addStartupScript';
import addClickHandler from '../events/addClickHandler';
import addTargetEventListener from '../events/addTargetEventListener';
import forEachSelector from '../elements/forEachSelector';
import getFormWizard from './elements/getFormWizard';
import toggleClass from '../elements/toggleClass';
import timezone from '../date/timezone';
import * as dateHelper from './date_helper';
import 'element-closest-polyfill';

import appointmentSchedulerContent from '../../templates/_appointment_scheduler_content.mustache';
import appointmentSchedulerError from '../../templates/_appointment_scheduler_error.mustache';
import appointmentSchedulerTOU from '../../templates/form_wizard/agree_to_terms_field.mustache';
import forEachElement from '../elements/forEachElement';
import getFieldValue from './elements/getFieldValue';

const APPOINTMENT_SCHEDULER_TARGET_SELECTOR = '.js-appointment-scheduler';
const DATE_SELECTOR = '.appointment-scheduler__dates input';
const TIME_SELECTOR = '.appointment-scheduler__times .appointment-scheduler__choice--time input';
const NEXT_BUTTON_SELECTOR = '.js-appointment-scheduler-next';
const TOU_CHECKMARK_SELECTOR = '.js-appointment-scheduler-tou';

const APPT_TIME_SELECTOR = '.appointment-summary__time-container';
const APPT_DATE_SELECTOR ='.appointment-summary__date-container';

const CURRENT_URL = window.location.href.split('#')[0];
const LEADFORM_SELECTOR = '.js-leadform';

export const saveUuid = (url) => {
  const uuidQuery = url.match(/(secure-uuid=)(.*?)(&|#|$)/);
  if (uuidQuery !== null) {
    return uuidQuery[2];
  } else {
    return false;
  }
};

/**
 * Returns an array of available time slots.
 *
 * returns:
 * [{timeslot: '2020-09-16T13:00:00:00'}, {...}]
 */
export const availableTimeSlots = async (url, LEADFORM_SELECTOR) => {
  const leadformContainer = document.querySelector(LEADFORM_SELECTOR);
  const uuid = getFieldValue(leadformContainer, 'uuid') || saveUuid(url);
  if (leadformContainer) {
    const domain = leadformContainer.dataset.domain;
    const timezoneIANA = new Intl.DateTimeFormat().resolvedOptions().timeZone.replace('/','@');
    if ( !uuid && timezoneIANA) {
      return timeslotFetch(domain, `/v1/todos/me/timeslots?tz=${timezoneIANA}`);
    } else if (uuid) {
      return timeslotFetch(domain, `/v1/todos/me/timeslots/${uuid}`);
    } else {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * Returns timeslots
 */
 export const timeslotFetch = (domain, endpoint) => fetch(`${domain}${endpoint}`)
  .then(response => response.json()).catch((error) => {
    const message = { 'message': `Scheduled callback fetch fail endpoint: ${endpoint}` };
    if(window.newrelic) {
      window.newrelic.noticeError(error, { 'customAttributes': message });
    }
});

export const formatTimeString = (thisTimeslot) => {
  let displayTime= moment(thisTimeslot).format('h:mm A');
  let timeRange = moment(thisTimeslot).add('15', 'minutes').format('h:mm A');

  if (document.querySelector(APPOINTMENT_SCHEDULER_TARGET_SELECTOR).dataset.timerange == 'true') {
    displayTime = displayTime + ' - ' + timeRange;
  }

  return displayTime;
};

export const getNowTime = () => {
  let baseTime = moment();
  let soonTime = baseTime.clone().add('5', 'minutes');

  const timeNow = {display: 'Now', value: soonTime.format()};
  const today = {
    display: 'Today',
    value: soonTime.format('YYYY-MM-DD'),
    times: []
  };

  let nowCallableTime = dateHelper.nowCallableTime(baseTime);

  if (nowCallableTime) {
    today.times.push(timeNow);
  }
  return today;
};

export const addNewSlots = (timeslots, alteredSlots, currentDate, dateIndex) => {
  alteredSlots.push(getNowTime());
  currentDate = alteredSlots[0].value;

  // timeslots should be an ordered data obj returned by API call
  for (let i = 0; i < timeslots.length; i++) {
    const thisTimeslot = timeslots[i].timeslot;
    const splitTimeslot = thisTimeslot.split('T');
    // Date generates date info like day of the week, month name, etc.
    const displayDate = new Date(splitTimeslot);
    let displayTime = formatTimeString(thisTimeslot);

    // if date doesn't exist in alteredSlots, push to array with first timeslot
    if (currentDate != splitTimeslot[0]) {
      const splitDisplayDate = displayDate.toString().split(' ');
      let labelDate = `${splitDisplayDate[0]}, ${splitDisplayDate[1]} ${splitDisplayDate[2]}`;

      if (splitDisplayDate[0] === 'Invalid') {
        let backupDate = splitTimeslot[0].split('-');
        labelDate = `${backupDate[1]}/${backupDate[2]}/${backupDate[0]}`;
      }

      alteredSlots.push({
        display: labelDate, value: splitTimeslot[0],
        times: [{ display: displayTime, value: thisTimeslot }]
      });

      dateIndex++;
    } else if (alteredSlots.length >= 1 && alteredSlots[dateIndex].value == splitTimeslot[0]) {
      //if date does exist in alteredSlots, just push new timeslot to existing object
      alteredSlots[dateIndex].times.push({ display: displayTime, value: thisTimeslot });
    }
    currentDate = splitTimeslot[0];
  }
  return alteredSlots;
};

/**
 * Returns an array of time slots, transformed in a format we can pass to mustache for rendering.
 *
 * returns:
 * [ { display: "Wed, Sep 16",
 *     times: [{
 *       display: "1:00 PM",
 *       value: "2020-09-16T13:00:00:00"
 *     }],
 *     value: "2020-09-16"
 *   },
 *   {...} ]
 */
export const transformedTimeslots = timeslots => {
  if (timeslots && timeslots.length) {
    let alteredSlots = [];
    let currentDate;
    let dateIndex = 0;
    return addNewSlots(timeslots, alteredSlots, currentDate, dateIndex);
  } else {
    return false;
  }
};

/**
 * Handles rendering results or showing the no results message if there are none.
 * Replaces the innerHTML of the entire target selector, so the loading icon
 * will disappear as well.
 *
 * @param {array} results
 */
export const renderResults = (results, targetSelector) => {
  const renderTarget = document.querySelector(targetSelector);

  if (!renderTarget) {
    return;
  } else if (
    renderTarget &&
    Object.keys(results) &&
    Object.keys(results).length > 0
  ) {
    const firstDate = results[0].value;
    if (firstDate.includes('2020-10-3')) {
      renderTarget.innerHTML = appointmentSchedulerContent({
        results: results,
        timezone: timezone.shortName(),
        dst_message: `All times are displayed in ${timezone.shortName()},
          please be aware that Daylight Savings Time ends on
          Sunday, November 1.`
      });
    } else {
      renderTarget.innerHTML = appointmentSchedulerContent({
        display_tou: renderTarget.dataset.tou,
        tou: appointmentSchedulerTOU,
        results: results,
        timezone: timezone.shortName()
      });
    }

  } else {
    renderTarget.innerHTML = appointmentSchedulerError({
      error_text: 'Sorry! We are fully booked, to contact an educational consultant please choose one of the following:',
      email_href: 'appointments@varsitytutors.com',
      email_text: 'Email to Schedule',
      phone_href: '+18009503697',
      phone_text: 'Call Us Now'
    });
  }
};

/**
 * Event handler to manage clicking next and populating appointment info
 */
export const clickedApptSchedulerNext = (event) => {
  const currentLeadform = getFormWizard(event.target);
  const selectedTime = currentLeadform.querySelector('input.appointment-scheduler__time-input:checked');
  const selectedDate = currentLeadform.querySelector('input.appointment-scheduler__date-input:checked');

  if (selectedTime && selectedTime.dataset.display) {
    forEachSelector(APPT_TIME_SELECTOR, apptTime => {
      if (selectedTime.dataset.display == 'Now'){
        apptTime.innerHTML = 'Thanks! We will call you ASAP';
      }
      else {
        apptTime.innerHTML = `<p>${selectedTime.dataset.display} (${timezone.shortName()})</p>`;
      }
    });
  }
  if (selectedDate && selectedDate.dataset.display) {
    forEachSelector(APPT_DATE_SELECTOR, apptDate => {
      if (selectedDate.dataset.display == 'Today' && selectedTime.dataset.display == 'Now'){
        apptDate.innerHTML = '';
      }
      else {
        apptDate.innerHTML = `<p>${selectedDate.dataset.display}</p>`;
      }
    });
  }
};

/**
 * Event handler to manage clicking dates
 */
export const clickedDate = el => {
  const newDateLabel = el.closest('label');
  const dataId = el.getAttribute('data-id');
  const currentTimes = document.querySelector(
    '.appointment-scheduler__times.active'
  );
  const newTimes = document.querySelector(
    `.appointment-scheduler__times[data-date=${dataId}]`
  );

  // replace primary button styles from all dates
  forEachSelector(DATE_SELECTOR, el => {
    let label = el.closest('label');
    toggleClass(label, 'button--secondary', true);
    toggleClass(label, 'button--primary', false);
  });

  // make new date primary
  toggleClass(newDateLabel, 'button--primary', true);
  toggleClass(newDateLabel, 'button--secondary', false);

  // move active class to new times
  if (newTimes) {
    toggleClass(newTimes, 'active', true);
    toggleClass(currentTimes, 'active', false);
  }
};

/**
 * Event handler to manage clicking times
 */
export const clickedTime = el => {
  const choice = el.closest('.appointment-scheduler__choice');
  const confirmButton = choice.querySelector(NEXT_BUTTON_SELECTOR);
  const touDiv = choice.querySelector(TOU_CHECKMARK_SELECTOR);
  const form = el.closest('form');

  // skip if we are already open
  if(!confirmButton.classList.contains('hidden')) { return; }

  // hide all next buttons
  forEachElement(form.querySelectorAll(NEXT_BUTTON_SELECTOR), el => {
    toggleClass(el, 'hidden', true);
  });

  // destroy all tou checkmarks and render the current one
  forEachElement(form.querySelectorAll(TOU_CHECKMARK_SELECTOR), el => {
    el.innerHTML = el === touDiv ? appointmentSchedulerTOU() : '';
  });

  // show desired next-button/tou adjacent to selected time
  toggleClass(confirmButton, 'hidden', false);
};

export function setup() {
  const apptBlock = document.querySelector(APPOINTMENT_SCHEDULER_TARGET_SELECTOR);
  if (apptBlock) {
    availableTimeSlots(CURRENT_URL, LEADFORM_SELECTOR).then(response => {
      renderResults(transformedTimeslots(response), APPOINTMENT_SCHEDULER_TARGET_SELECTOR);

      forEachSelector(DATE_SELECTOR, el =>
        addClickHandler(el, () => clickedDate(el))
      );

      forEachSelector(TIME_SELECTOR, el =>
        addClickHandler(el, () => clickedTime(el))
      );

      // select first date
      const firstDate = document.querySelector(DATE_SELECTOR);
      if (firstDate) {
        firstDate.click();
      }
    });
  }
}

addStartupScript(setup);

addTargetEventListener('click', NEXT_BUTTON_SELECTOR, (event) => {
  clickedApptSchedulerNext(event);
});
